/**
 * HuduSetupForm
 *
 * Used by the onboarding wizard to let an MSP connect the Hudu documentation platform.
 * Setup copy matches Add Integration via shared `HuduSetupGuideSteps`.
 * This form mirrors integration behavior for:
 * - Test connectivity via `POST /platforms/hudu/test`
 * - Save via `POST /platforms` (docs platform row)
 *
 * Note: Hudu config uses `base_url` and `api_key` keys.
 */
import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IntegrationApi from "../apis/integrationApi";
import { HuduSetupGuideSteps } from "./integrationGuides/HuduSetupGuideSteps";

interface HuduSetupFormProps {
  mspId: number;
  onBack?: () => void;
  onSuccess?: () => void;
  onSkip?: () => void;
}

const HuduSetupForm: React.FC<HuduSetupFormProps> = ({ mspId, onBack, onSuccess, onSkip }) => {
  const integrationApi = useMemo(() => new IntegrationApi(), []);

  const [baseUrl, setBaseUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  const testedValuesRef = useRef<{
    baseUrl: string;
    apiKey: string;
  } | null>(null);

  const normalizeBaseUrl = (raw: string): string => {
    let val = String(raw || "").trim();
    if (!val) return "";
    if (!val.startsWith("https://") && !val.startsWith("http://")) {
      val = `https://${val}`;
    }
    val = val.replace(/\/+$/, "");
    if (!val.endsWith("/api/v1")) {
      val = `${val}/api/v1`;
    }
    return val;
  };

  const hasFieldsChanged = () => {
    if (!testedValuesRef.current || !testResult?.success) return true;
    return (
      apiKey !== testedValuesRef.current.apiKey ||
      baseUrl !== testedValuesRef.current.baseUrl
    );
  };

  const isSaveDisabled = !testResult?.success || hasFieldsChanged() || isSaving;

  const handleTestConnection = async () => {
    const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
    if (!normalizedBaseUrl || !apiKey) {
      setTestResult({
        success: false,
        message: "Please fill in all fields before testing the connection.",
      });
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await integrationApi.testPlatformConfigurationDirect("hudu", {
        base_url: normalizedBaseUrl,
        api_key: apiKey,
      });

      // The backend returns { success, message, details }.
      const nextSuccess = Boolean(result?.success ?? true);
      if (nextSuccess) {
        testedValuesRef.current = {
          baseUrl: baseUrl.trim(),
          apiKey,
        };
      }

      setTestResult({
        success: nextSuccess,
        message: result?.message || "Connection test successful!",
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Connection test failed. Please check your credentials.";
      setTestResult({
        success: false,
        message: errorMessage,
      });
      testedValuesRef.current = null;
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    if (isSaveDisabled) return;

    setIsSaving(true);
    try {
      const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
      await integrationApi.createPlatform(mspId, {
        type: "docs",
        name: "hudu",
        config_json: {
          base_url: normalizedBaseUrl,
          api_key: apiKey,
        },
      });

      setShowSuccessSnackbar(true);
      if (onSuccess) {
        setTimeout(() => onSuccess(), 1500);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save Hudu configuration.";
      setTestResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      {onBack && (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            fontFamily: "var(--theo-font-sans)",
            color: "#6c6c6c",
            textTransform: "none",
            mb: 3,
            "&:hover": {
              background: "#f5f5f5",
            },
          }}
        >
          Back
        </Button>
      )}

      <Typography
        variant="h5"
        component="h2"
        sx={{
          fontFamily: "var(--theo-font-sans)",
          fontWeight: 700,
          mb: 3,
          color: "#1a1a1a",
        }}
      >
        Hudu Setup
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          background: "#f8fafc",
          borderRadius: 2,
          border: "1px solid #e5e5e5",
        }}
      >
        <HuduSetupGuideSteps />
      </Paper>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          label="Base URL"
          value={baseUrl}
          onChange={(e) => {
            setBaseUrl(e.target.value);
            if (testResult?.success) setTestResult(null);
          }}
          placeholder="https://your-hudu-domain.com/api/v1"
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": { fontFamily: "var(--theo-font-sans)" },
            "& .MuiInputBase-input": { fontFamily: "var(--theo-font-sans)" },
          }}
          helperText="Your Hudu API base URL"
        />

        <TextField
          label="API Key"
          type="password"
          value={apiKey}
          onChange={(e) => {
            setApiKey(e.target.value);
            if (testResult?.success) setTestResult(null);
          }}
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": { fontFamily: "var(--theo-font-sans)" },
            "& .MuiInputBase-input": { fontFamily: "var(--theo-font-sans)" },
          }}
          helperText="Your Hudu API key"
        />

        {testResult && (
          <Alert
            severity={testResult.success ? "success" : "error"}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              "& .MuiAlert-message": { fontFamily: "var(--theo-font-sans)" },
            }}
          >
            {testResult.message}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Button
            variant="outlined"
            onClick={handleTestConnection}
            disabled={isTesting || isSaving}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              fontWeight: 500,
              borderColor: "#705d4a",
              color: "#705d4a",
              "&:hover": {
                borderColor: "#604d3a",
                background: "#f5f5f5",
              },
              minWidth: 180,
            }}
          >
            {isTesting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Testing...
              </>
            ) : (
              "Test Connection"
            )}
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaveDisabled}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              fontWeight: 500,
              background: isSaveDisabled ? "#e0e0e0" : "#705d4a",
              color: isSaveDisabled ? "#9e9e9e" : "#fff",
              minWidth: 150,
              "&:hover": {
                background: isSaveDisabled ? "#e0e0e0" : "#604d3a",
              },
            }}
          >
            {isSaving ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>

          {onSkip && (
            <Button
              variant="text"
              onClick={onSkip}
              sx={{
                fontFamily: "var(--theo-font-sans)",
                textTransform: "none",
                fontWeight: 500,
                color: "#6c6c6c",
                "&:hover": {
                  background: "#f5f5f5",
                },
              }}
            >
              Skip for now
            </Button>
          )}
        </Box>
      </Box>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          sx={{ fontFamily: "var(--theo-font-sans)" }}
        >
          Hudu has been successfully connected!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HuduSetupForm;

